import { ParentSize } from "@visx/responsive";
import { Group } from "@visx/group";
import { cn } from "@rosette/ts/src/util/cn.js";
import { motion, AnimatePresence, useIsPresent } from "framer-motion";
import { useEffect, useMemo, useRef, useState } from "react";

interface DiceNodeGraphProps {
    className?: string;
    graphClassName?: string;
    diceRolls: (number | null)[];
    numColumns?: number;
}

/* ----------  layout/constants  ---------- */

const EDGE_GAP = 2;
const SELECTED_OPACITY = 1.0;
const UNSELECTED_OPACITY = 0.25;
const TOTAL_ROWS = 5;

/* ----------  helpers  ---------- */

function nodePos(
    width: number,
    height: number,
    col: number,
    row: number,
    radius: number,
    numColumns: number,
) {
    if (col === 0) {
        return { x: radius, y: height / 2 };
    }
    const xGap = (width - 2 * radius) / (numColumns - 1);
    const x = radius + col * xGap;
    const spread = height / (TOTAL_ROWS - 1);
    const y = row * spread;
    return { x, y };
}

function edgeEndpoints(
    start: { x: number; y: number },
    end: { x: number; y: number },
    radius: number,
    gap: number,
) {
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const len = Math.hypot(dx, dy) || 1;
    const ux = dx / len;
    const uy = dy / len;
    return {
        x1: start.x + ux * (radius + gap),
        y1: start.y + uy * (radius + gap),
        x2: end.x - ux * (radius + gap),
        y2: end.y - uy * (radius + gap),
    };
}

/* ----------  SVG primitives  ---------- */

interface NodeProps {
    x: number;
    y: number;
    selected: boolean;
    radius: number;
}
const Node = ({ x, y, selected, radius }: NodeProps) => (
    <motion.circle
        cx={x}
        cy={y}
        r={radius}
        className={selected ? "fill-primary-strong stroke-text-strong" : "fill-text-on-light stroke-text-strong"}
        strokeWidth={2}
        initial={{ opacity: selected ? SELECTED_OPACITY : 0 }}
        animate={{ opacity: selected ? SELECTED_OPACITY : UNSELECTED_OPACITY }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.35 }} />
);

interface EdgeProps {
    x1: number;
    y1: number;
    x2: number;
    y2: number;
    selected: boolean;
    appear: boolean;
}

const Edge = ({ x1, y1, x2, y2, selected, appear }: EdgeProps) => {
    const isPresent = useIsPresent();
    const initialProps = appear
        ? { pathLength: 0, opacity: 0 }
        : { pathLength: 1, opacity: UNSELECTED_OPACITY };

    const animateProps = {
        pathLength: 1,
        opacity: selected ? SELECTED_OPACITY : UNSELECTED_OPACITY,
    };

    const transitionProps = selected || !isPresent
        ? { duration: 0.3, ease: "easeInOut" as const }
        : { duration: appear ? 0.6 : 0.3, ease: "easeInOut" as const };

    return (
        <motion.line
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            className="stroke-text-strong"
            strokeWidth={2}
            vectorEffect="non-scaling-stroke"
            initial={initialProps}
            animate={animateProps}
            exit={{ opacity: 0, pathLength: 1 }}
            transition={transitionProps} />
    );
};

interface EdgeWithLabelProps extends EdgeProps {
    label: string;
}

const EdgeWithLabel = ({
    label,
    x1,
    y1,
    x2,
    y2,
    selected,
    appear,
}: EdgeWithLabelProps) => {
    const isPresent = useIsPresent();
    const dx = x2 - x1;
    const dy = y2 - y1;
    const len = Math.hypot(dx, dy) || 1;
    const ux = dx / len;
    const uy = dy / len;
    const mx = x1 * 0.45 + x2 * 0.55;
    const my = y1 * 0.45 + y2 * 0.55;
    const cut = 24;
    const ax = mx - ux * cut;
    const ay = my - uy * cut;
    const bx = mx + ux * cut;
    const by = my + uy * cut;
    const angle = (Math.atan2(dy, dx) * 180) / Math.PI;

    return (
        <g>
            <Edge
                key="part1"
                x1={x1}
                y1={y1}
                x2={ax}
                y2={ay}
                selected={selected}
                appear={appear} />

            <Edge
                key="part2"
                x1={bx}
                y1={by}
                x2={x2}
                y2={y2}
                selected={selected}
                appear={appear} />

            <motion.text
                key="label"
                x={mx}
                y={my}
                transform={`rotate(${angle}, ${mx}, ${my})`}
                className="fill-text-strong"
                fontSize={12}
                textAnchor="middle"
                dominantBaseline="middle"
                initial={{ opacity: appear && !selected ? 0 : UNSELECTED_OPACITY }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: isPresent ? 0.6 : 0.3 }}>

                {label}
            </motion.text>
        </g>
    );
};

/* ----------  Internal graph  ---------- */

interface InternalProps {
    width: number;
    height: number;
    diceRolls: (number | null)[];
    numColumns: number;
    className?: string;
}

function InternalDiceNodeGraph({
    width,
    height,
    diceRolls,
    numColumns,
    className,
}: InternalProps) {
    const MARGIN = 20;
    const availableWidth = width - 2 * MARGIN;
    const availableHeight = height - 2 * MARGIN;
    const spread = availableHeight / (TOTAL_ROWS - 1);
    const NODE_RADIUS = Math.max(
        1,
        Math.min(availableWidth / numColumns, spread) / 2 - EDGE_GAP,
    );

    /* ----------  track previous rolls  ---------- */
    const prevRollsRef = useRef<(number | null)[]>([]);
    const [generation, setGeneration] = useState(0);
    const didAddRoll = diceRolls.length > prevRollsRef.current.length;

    useEffect(() => {
        if (diceRolls.length < prevRollsRef.current.length) {
            setGeneration(gen => gen + 1);
        }
        prevRollsRef.current = diceRolls;
    }, [diceRolls, setGeneration]);

    /* ----------  nodes  ---------- */
    const nodes = useMemo(() => {
        const list = [{ col: 0, row: 2, key: `n-g${generation}-c0-root`, selected: true }];

        for (let col = 1; col < numColumns; ++col) {
            if (diceRolls.length >= col) {
                const rollIndex = col - 1;
                const chosen = diceRolls[rollIndex];
                for (let r = 0; r < TOTAL_ROWS; ++r) {
                    list.push({
                        col,
                        row: r,
                        key: `n-g${generation}-c${col}-${r}`,
                        selected: chosen !== null && r === chosen,
                    });
                }
            }
        }

        return list.map(n => ({
            ...n,
            ...nodePos(
                availableWidth,
                availableHeight,
                n.col,
                n.row,
                NODE_RADIUS,
                numColumns,
            ),
        }));
    }, [generation, availableWidth, availableHeight, NODE_RADIUS, numColumns, diceRolls]);

    const nodeByKey = useMemo(
        () => Object.fromEntries(nodes.map(n => [n.key, n])),
        [nodes],
    );

    /* ----------  edges  ---------- */
    const edges = useMemo(() => {
        const list = [];

        if (diceRolls.length >= 1) {
            for (let r = 0; r < TOTAL_ROWS; ++r) {
                list.push({
                    fromKey: `n-g${generation}-c0-root`,
                    toKey: `n-g${generation}-c1-${r}`,
                    selected: diceRolls[0] !== null && r === diceRolls[0],
                    appear: didAddRoll && diceRolls.length === 1,
                    key: `e-g${generation}-root-1-${r}`,
                });
            }
        }

        for (let col = 2; col < numColumns; ++col) {
            if (diceRolls.length >= col) {
                const prevRoll = diceRolls[col - 2] as number;
                for (let r = 0; r < TOTAL_ROWS; ++r) {
                    list.push({
                        fromKey: `n-g${generation}-c${col - 1}-${prevRoll}`,
                        toKey: `n-g${generation}-c${col}-${r}`,
                        selected:
                            diceRolls[col - 1] !== null && r === diceRolls[col - 1],
                        appear: didAddRoll && diceRolls.length === col,
                        key: `e-g${generation}-c${col - 1}-${prevRoll}-${r}`,
                    });
                }
            }
        }
        return list;
    }, [generation, diceRolls, numColumns, didAddRoll]);

    if (width < 10) return null;
    return (
        <svg className={className} width={width} height={height}>
            <Group top={MARGIN} left={MARGIN}>
                <AnimatePresence>
                    {edges.map(({ fromKey, toKey, selected, appear, key }) => {
                        const from = nodeByKey[fromKey];
                        const to = nodeByKey[toKey];
                        if (!from || !to) return null;
                        const { x1, y1, x2, y2 } = edgeEndpoints(
                            from,
                            to,
                            NODE_RADIUS,
                            EDGE_GAP,
                        );

                        if (selected) {
                            const parts = toKey.split("-");
                            const r = parseInt(parts[parts.length - 1], 10);
                            return (
                                <EdgeWithLabel
                                    key={key}
                                    label={`Roll ${r}`}
                                    x1={x1}
                                    y1={y1}
                                    x2={x2}
                                    y2={y2}
                                    selected={selected}
                                    appear={appear} />
                            );
                        }

                        return (
                            <Edge
                                key={key}
                                x1={x1}
                                y1={y1}
                                x2={x2}
                                y2={y2}
                                selected={selected}
                                appear={appear} />
                        );
                    })}

                    {nodes.map(({ key, x, y, selected }) => (
                        <Node
                            key={key}
                            x={x}
                            y={y}
                            selected={selected}
                            radius={NODE_RADIUS} />
                    ))}
                </AnimatePresence>
            </Group>
        </svg>
    );
}

/* ----------  exported wrapper  ---------- */

export function DiceNodeGraph({
    className,
    graphClassName,
    diceRolls,
    numColumns = 5,
}: DiceNodeGraphProps) {
    return (
        <figure className={cn("mt-0 mb-0", className ?? null)}>
            <div className="w-full h-32 md:h-40 select-none">
                <ParentSize>
                    {({ width, height }) => (
                        <InternalDiceNodeGraph
                            width={width}
                            height={height}
                            diceRolls={diceRolls}
                            numColumns={numColumns}
                            className={graphClassName} />
                    )}
                </ParentSize>
            </div>
        </figure>
    );
}
